/* Shared helpers for the admin dashboard. Pulled out of AdminDashboard.jsx so
 * the (now tabbed) LeadDrawer and the MonthlyLeadsChart can reuse them without
 * a circular import. */

export const OFFER_LABELS = {
  "free-pos-offer": "Free POS",
  "cash-discount-savings": "Cash Discount",
  "savings-analysis": "Savings Analysis",
  "get-started": "Get Started",
  "contact": "Contact",
  "pos-quote": "POS Quote",
};

/* Canonical status list — order matters (Leads dropdown + Reports order).
 * Note: legacy `statement_needed` is normalised to `info_needed` everywhere
 * via the normalizeStatus() helper below — so old leads keep displaying
 * correctly without a DB migration. New writes always use `info_needed`. */
export const STATUSES = [
  { v: "new", label: "New Lead", color: "bg-[var(--swipe-soft-blue)] text-[var(--swipe-accent)] border-[var(--swipe-accent)]/30" },
  { v: "info_needed", label: "Info Needed", color: "bg-[#FACC15] text-[#7C2D12] border-[#CA8A04]" },
  { v: "contacted", label: "Contacted", color: "bg-[#BBF7D0] text-[#14532D] border-[#86EFAC]" },
  { v: "proposal_sent", label: "Proposal Sent", color: "bg-[#86EFAC] text-[#14532D] border-[#22C55E]" },
  { v: "follow_up", label: "Follow-Up", color: "bg-[#22C55E] text-white border-[#15803D]" },
  { v: "closed_won", label: "Closed Won", color: "bg-[#15803D] text-white border-[#166534]" },
  { v: "closed_lost", label: "Closed Lost", color: "bg-[#991B1B] text-white border-[#7F1D1D]" },
];

/* Maps the legacy `statement_needed` enum value to its new replacement
 * `info_needed`. Use everywhere the UI reads l.status before rendering or
 * before matching against the STATUSES list. */
export function normalizeStatus(value) {
  if (value === "statement_needed") return "info_needed";
  return value || "new";
}

export const PRIORITIES = [
  { v: "hot", label: "Hot Lead", color: "bg-rose-50 text-rose-700 border-rose-200", dot: "bg-rose-500" },
  { v: "warm", label: "Warm Lead", color: "bg-amber-50 text-amber-700 border-amber-200", dot: "bg-amber-500" },
  { v: "cold", label: "Cold Lead", color: "bg-slate-100 text-slate-600 border-slate-300", dot: "bg-slate-400" },
];

export const ACTIVITY_TYPES = [
  { v: "call", label: "Call", group: "follow_up" },
  { v: "text", label: "Text", group: "follow_up" },
  { v: "email", label: "Email", group: "follow_up" },
  { v: "meeting", label: "Meeting", group: "appointment" },
  { v: "in-person", label: "In-Person", group: "appointment" },
  { v: "zoom", label: "Zoom", group: "appointment" },
  { v: "other", label: "Other", group: "follow_up" },
];

export function statusMeta(v) {
  const normalized = normalizeStatus(v);
  return STATUSES.find((s) => s.v === normalized) || STATUSES[0];
}
export function priorityMeta(v) {
  return PRIORITIES.find((p) => p.v === v) || PRIORITIES[1];
}
export function activityMeta(v) {
  return ACTIVITY_TYPES.find((a) => a.v === v) || ACTIVITY_TYPES[6];
}

export function isSnoozed(lead) {
  const sz = lead?.snooze_until;
  if (!sz) return false;
  try { return new Date(sz).getTime() > Date.now(); } catch { return false; }
}
export function formatSnoozeUntil(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}
export function isStale(lead) {
  if (lead?.status !== "new" || !lead?.created_at) return false;
  if (isSnoozed(lead)) return false;
  try {
    const age = Date.now() - new Date(lead.created_at).getTime();
    return age >= 24 * 60 * 60 * 1000;
  } catch { return false; }
}

export const digits = (p) => (p || "").replace(/[^\d+]/g, "");

/* Named time + value thresholds — keeps the rules below readable. */
const DAY_IN_MS = 86400000;                 // 24 × 60 × 60 × 1000
const HIGH_VALUE_VOLUME_THRESHOLD = 50000;  // monthly processing $50K+ → high value
const HIGH_VALUE_ANNUAL_THRESHOLD = 30000;  // ~1 year of meaningful margin

export function lastTouchedBadge(lead) {
  const lt = lead?.last_touched_at;
  if (!lt) return { label: "Never Contacted", cls: "bg-rose-50 text-rose-700 border-rose-200" };
  const days = Math.floor((Date.now() - new Date(lt).getTime()) / DAY_IN_MS);
  if (days <= 0) return { label: "Today", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" };
  if (days === 1) return { label: "Yesterday", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" };
  if (days <= 2) return { label: `${days}d ago`, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" };
  if (days <= 6) return { label: `${days}d ago`, cls: "bg-amber-50 text-amber-700 border-amber-200" };
  return { label: `${days}d ago`, cls: "bg-rose-50 text-rose-700 border-rose-200" };
}

export function isHighVolume(s) {
  if (!s || typeof s !== "string") return false;
  const v = s.toLowerCase().replace(/[,\s]/g, "");
  const matches = [...v.matchAll(/\$?(\d+(?:\.\d+)?)(k|m)?/g)].map((m) => {
    const num = parseFloat(m[1]);
    if (m[2] === "k") return num * 1000;
    if (m[2] === "m") return num * 1000000;
    return num;
  });
  if (!matches.length) return false;
  return Math.max(...matches) >= HIGH_VALUE_VOLUME_THRESHOLD;
}

/* Lead is high-value if either:
 * - parsed monthly volume label says $50K+, OR
 * - monthly_volume_amount >= HIGH_VALUE_VOLUME_THRESHOLD, OR
 * - est_annual_value >= HIGH_VALUE_ANNUAL_THRESHOLD (~ a year of meaningful processing margin). */
export function isHighValue(lead) {
  if (isHighVolume(lead?.monthly_volume)) return true;
  if ((lead?.monthly_volume_amount || 0) >= HIGH_VALUE_VOLUME_THRESHOLD) return true;
  if ((lead?.est_annual_value || 0) >= HIGH_VALUE_ANNUAL_THRESHOLD) return true;
  return false;
}

export function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
}

export function todayISO() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

/* Returns tomorrow as YYYY-MM-DD in the local browser timezone — used by the
 * "Snooze 1 day" button on overdue calendar items. */
export function tomorrowISO() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

/* Compute a Date for a scheduled activity (date + optional time). */
function activityDateTime(act) {
  if (!act?.date) return null;
  const t = (act.time && /^\d{1,2}:\d{2}$/.test(act.time)) ? act.time : "23:59";
  try { return new Date(`${act.date}T${t}:00`); } catch { return null; }
}

export function isActivityOverdue(act) {
  if (!act || act.status !== "pending") return false;
  const dt = activityDateTime(act);
  return dt ? dt.getTime() < Date.now() : false;
}

export function isActivityDueToday(act) {
  if (!act || act.status !== "pending" || !act.date) return false;
  return act.date === todayISO();
}

export function nextPendingActivity(lead) {
  const acts = (lead?.scheduled_activities || []).filter((a) => a.status === "pending");
  if (!acts.length) return null;
  acts.sort((a, b) => {
    const da = activityDateTime(a)?.getTime() ?? Infinity;
    const dbb = activityDateTime(b)?.getTime() ?? Infinity;
    return da - dbb;
  });
  return acts[0];
}

export function formatActivityWhen(act) {
  if (!act?.date) return "—";
  try {
    const t = (act.time && /^\d{1,2}:\d{2}$/.test(act.time)) ? act.time : null;
    const dt = new Date(`${act.date}T${t || "00:00"}:00`);
    const dateStr = dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    if (!t) return dateStr;
    const timeStr = dt.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
    return `${dateStr} · ${timeStr}`;
  } catch { return act.date; }
}

/* Pool the visible source labels for the source-filter dropdown. */
export function collectSources(leads) {
  const set = new Set();
  for (const l of leads || []) set.add(l.source_label || "Direct / Unknown");
  const arr = Array.from(set);
  arr.sort((a, b) => {
    if (a === "Direct / Unknown") return 1;
    if (b === "Direct / Unknown") return -1;
    return a.localeCompare(b);
  });
  return arr;
}

export const APPOINTMENT_TYPES = new Set(["meeting", "in-person", "zoom"]);
export const FOLLOWUP_TYPES = new Set(["call", "text", "email", "other"]);
